// src/components/resources/tools/generators/ReviewMockup/types.ts
//
// Shared type definitions + default config for the Review Mockup Generator.
// Extracted verbatim from the former single-file component (A11b decomposition)
// so the generator's platform-card subcomponents and the pure App Store review
// assembly helper can share one source of truth.

/**
 * Supported review platforms.
 */
export type ReviewPlatform = 'google' | 'appstore' | 'yelp'

/**
 * Allowed rating values.
 */
export type Rating = 1 | 2 | 3 | 4 | 5

/**
 * A single App Store "Ratings & Reviews" entry. The App Store product page
 * stacks several of these under the ratings summary — unlike the Google/Yelp
 * single-card layouts — so the App Store variant models them as an array.
 */
export interface AppStoreReviewItem {
  title: string
  body: string
  rating: Rating
  reviewerName: string
  date: string
}

/**
 * App-Store-specific product-page config. Namespaced under `ReviewConfig`
 * so the Google/Yelp single-card layouts don't carry these fields. Models
 * the full App Store product page: the app-header row, the 4-stat summary
 * strip, the aggregate rating summary + star-distribution histogram, and the
 * stacked review list. Every field is editable via the App-Store-only control
 * group so the rendered layout matches the marketing hero image honestly.
 */
export interface AppStoreConfig {
  /** App display name in the header row (e.g. "Design Studio"). */
  appName: string
  /** Subtitle/tagline under the app name (e.g. "Mockups made simple."). */
  tagline: string
  /** Optional uploaded app-icon data URL; falls back to a gradient tile. */
  icon?: string
  /** Aggregate average shown large beside the stars (e.g. 4.8). */
  ratingAverage: number
  /** Total ratings count label, kept as a string for "128K" / "2.3K". */
  ratingCount: string
  /**
   * 5★→1★ distribution as relative weights (any positive scale; bars are
   * normalized to the max). Drives the histogram beside the average.
   */
  ratingDistribution: [number, number, number, number, number]
  /** Age-rating stat (e.g. "4+"). */
  ageRating: string
  /** Chart-rank stat (e.g. "#47"). */
  chartRank: string
  /** Chart category under the rank (e.g. "Graphics & Design"). */
  chartCategory: string
  /**
   * Additional reviews stacked below the primary review. The primary review
   * is driven by the shared `reviewTitle`/`reviewText`/`rating`/`reviewerName`
   * /`date` fields so the existing controls keep working; these are the
   * extra entries the App Store page shows beneath it.
   */
  additionalReviews: AppStoreReviewItem[]
}

/**
 * Configuration for a review mockup.
 */
export interface ReviewConfig {
  platform: ReviewPlatform
  businessName: string
  reviewerName: string
  reviewTitle: string
  reviewText: string
  rating: Rating
  date: string
  avatar?: string
  /**
   * Spotlight glow — wraps the preview card in a soft elevated glow on the
   * dark export backdrop. Platform-agnostic polish; honored by every layout.
   */
  highlight: boolean
  /**
   * Yelp "Elite" reviewer badge toggle. Only the Yelp layout renders a
   * reviewer badge, so this control is shown only for Yelp.
   */
  includeBadge: boolean
  location: string
  /** App-Store-only product-page fields (see {@link AppStoreConfig}). */
  appStore: AppStoreConfig
}

/**
 * Default configuration for a review mockup.
 */
export const DEFAULT_CONFIG: ReviewConfig = {
  platform: 'google',
  businessName: 'AdSights',
  reviewerName: 'Jordan Lee',
  reviewTitle: 'Transformed our paid media workflow',
  reviewText:
    'The AdSights suite has completely reshaped how we plan and launch campaigns. Creative mockups are client-ready in minutes and reporting templates are genuinely executive level.',
  rating: 5,
  date: 'March 12, 2025',
  highlight: true,
  includeBadge: true,
  location: 'San Francisco, CA',
  appStore: {
    appName: 'AdSights',
    tagline: 'Marketing mockups, made simple.',
    ratingAverage: 4.8,
    ratingCount: '128K',
    ratingDistribution: [80, 12, 4, 2, 2],
    ageRating: '4+',
    chartRank: '#12',
    chartCategory: 'Business',
    additionalReviews: [
      {
        title: 'Exactly what our team needed',
        body: 'Clean, fast, and the exports drop straight into our decks. Has replaced three other tools for us.',
        rating: 5,
        reviewerName: 'Mariana T.',
        date: '1w ago',
      },
      {
        title: 'Huge time-saver',
        body: 'Spun up a full set of social-proof mockups for a launch in an afternoon. Wish I had found it sooner.',
        rating: 5,
        reviewerName: 'devlin_makes',
        date: '2w ago',
      },
    ],
  },
}
